// 📄 context/ProjectContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { subscribeProject, logAdminAccess, Project, ProjectRole } from '../services/projectService.ts';
import { useAuth } from './AuthContext.tsx';
import { useOrganizations } from '../hooks/useOrganizations.ts';
import { registrarProyectoVisitado } from '../services/recentesLocalService.ts';

interface ProjectContextValue {
  projectId: string;
  project: Project | null;
  loading: boolean;
  // Rol del usuario autenticado en ESTE proyecto (null si aún está
  // cargando o no es miembro). `esCliente` es el choke point que usa
  // toda la UI para pasar a modo solo lectura.
  miRol: ProjectRole | null;
  esCliente: boolean;
  // Admin de la plataforma viendo un proyecto donde NO es miembro real
  // (ver "Vista Admin" en MisProyectos.tsx) — a diferencia de `esCliente`,
  // que es un ROL real dentro del proyecto, esto es "no tiene ningún rol
  // acá, solo entró por ser admin". Se usa para ocultar acciones que
  // requieren ser miembro de verdad (invitar, registrar horas propias,
  // reportar incidentes) aunque `esCliente` sea false — las reglas de
  // Firestore ya rechazarían esas escrituras igual, esto es la UI
  // reflejando lo mismo sin dejar que el usuario intente en vano.
  esAdminObservador: boolean;
  // Sub-fase E (refactor de Organizaciones), Opción C — ampliada a Admin de
  // org el mismo día que M1+M2, a pedido explícito: true si soy OWNER o
  // ADMIN de la organización dueña de ESTE proyecto, viéndolo SIN ser
  // miembro real (miRol sigue null). Eje ORTOGONAL a esAdminObservador — un
  // usuario puede ser ambos, uno, o ninguno; no se fusionan. A diferencia
  // del admin de PLATAFORMA (bypass de solo lectura), owner/admin de
  // ORGANIZACIÓN SÍ tienen escritura colaborativa en incidentes/catálogos
  // técnicos (ver firestore.rules, esOwnerOAdminDeOrg) — por eso la UI NO
  // debe usar esta bandera para ocultar esas acciones como hace con
  // esAdminObservador, solo para explicar/etiquetar el acceso si hace
  // falta (la decisión aprobada es "sin badge especial, acceso normal a
  // sus proyectos"). Se llamó esOrgOwnerObservador hasta que dejó de ser
  // exclusivo del owner — renombrada acá mismo.
  esOrgAdminObservador: boolean;
}

const ProjectContext = createContext<ProjectContextValue>({
  projectId: '',
  project: null,
  loading: true,
  miRol: null,
  esCliente: false,
  esAdminObservador: false,
  esOrgAdminObservador: false
});

// Se monta dentro de una ruta con :projectId (/proyecto/:projectId/*) y
// reemplaza el viejo patrón getAuth()/onAuthStateChanged de los hooks de
// integración: ahora el progreso se identifica por proyecto, no por
// usuario autenticado.
export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, profile } = useAuth();
  const { organizations } = useOrganizations();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  // Dedupe del log de auditoría: una vez por proyecto y por sesión de
  // pestaña alcanza (no hace falta un doc nuevo en cada re-render/snapshot).
  const logueado = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!projectId) {
      setProject(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = subscribeProject(projectId, (p) => {
      setProject(p);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [projectId]);

  const miRol = (project && user)
    ? (project.miembros.find(m => m.userId === user.uid)?.rol ?? null)
    : null;
  const esCliente = miRol === 'cliente';
  const esAdminObservador = profile?.role === 'admin' && miRol === null && !!project;
  // organizations viene de useOrganizations() (Sub-fase A) — ya es "mis
  // organizaciones", así que basta confirmar que ESTA es una de ellas Y
  // que mi rol ahí es owner o admin (Organization.role, de Sub-fase G1).
  const esOrgAdminObservador = !!project && miRol === null &&
    organizations.some(org => org.id === project.orgId && (org.role === 'owner' || org.role === 'admin'));

  useEffect(() => {
    if (!esAdminObservador || !user || !projectId) return;
    if (logueado.current.has(projectId)) return;
    logueado.current.add(projectId);
    logAdminAccess(user.uid, projectId).catch((err) => console.error('Error registrando acceso admin:', err));
  }, [esAdminObservador, user, projectId]);

  // "Accesos rápidos" del Home (2026-08-20) — registra la visita SOLO
  // cuando el usuario es miembro real (no un admin/owner de org solo
  // observando, ni un admin de plataforma de paso) para que la lista de
  // "recientes" refleje proyectos propios, no cualquiera que se haya
  // mirado una vez. Ver recentesLocalService.ts.
  useEffect(() => {
    if (!project || !user || miRol === null) return;
    registrarProyectoVisitado(user.uid, project.id);
  }, [project, user, miRol]);

  return (
    <ProjectContext.Provider value={{ projectId: projectId || '', project, loading, miRol, esCliente, esAdminObservador, esOrgAdminObservador }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  return useContext(ProjectContext);
}
